import React, { useState, useEffect, useRef, useMemo } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { IconButton } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { motion, AnimatePresence } from "motion/react";
import backArrowIcon from "../assets/images/back-arrow.svg";
import loader from "../assets/gifs/loader.gif";
import folderIcon from "../assets/images/folder-icon.svg";
import GradeFolders from "./components/PdfUpload/GradeFolders";
import GradeFilesTable from "./components/PdfUpload/GradeFilesTable";
import OmrUploadDialog from "../shared/components/UploadModal/OmrUploadDialog";
import { setSelectedGrade } from "../store/omrUpload/omrUpload.slice";
import styles from "./PdfUploadPage.module.css";
import formatSchoolAddress from "../shared/utils/formatSchoolAddress";
import useOmrUploadData from "../store/omrUpload/useOmrUploadData";

const NotificationPortal = ({ children }) => {
  return ReactDOM.createPortal(children, document.body);
};

const PdfUploadPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { selectedSchool, selectedEvent, selectedGrade } = useSelector(
    (state) => state.omrUpload
  );
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");
  const [isFilesView, setIsFilesView] = useState(selectedGrade !== null);
  const [foldersKey, setFoldersKey] = useState(0);
  const [overlayRect, setOverlayRect] = useState(null);
  const [isFoldersFading, setIsFoldersFading] = useState(false);
  const [shouldRenderFolders, setShouldRenderFolders] = useState(
    selectedGrade === null
  );
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const contentRef = useRef(null);

  // Redirect if no context
  useEffect(() => {
    if (!selectedSchool || !selectedEvent) {
      navigate("/students");
    }
  }, [selectedSchool, selectedEvent, navigate]);

  const shouldFetchUploads =
    Boolean(selectedEvent) && selectedGrade !== null && selectedGrade !== "";
  const apiPage = paginationModel.page + 1;
  const apiLimit = paginationModel.pageSize;

  const { grades, uploads, isUploadsLoading, refreshUploads, totalCount } =
    useOmrUploadData({
      fetchUploads: shouldFetchUploads,
      gradeId: shouldFetchUploads ? selectedGrade : undefined,
      page: shouldFetchUploads ? apiPage : 1,
      limit: apiLimit,
    });

  const files = useMemo(() => {
    if (selectedGrade === null) return [];
    return uploads;
  }, [uploads, selectedGrade]);

  useEffect(() => {
    if (
      selectedGrade !== null &&
      grades.length > 0 &&
      !grades.includes(Number(selectedGrade))
    ) {
      dispatch(setSelectedGrade(null));
    }
  }, [grades, selectedGrade, dispatch]);

  useEffect(() => {
    if (selectedGrade === null) {
      setIsFilesView(false);
      setShouldRenderFolders(true);
      setIsFoldersFading(false);
    }
    setPaginationModel((prev) => ({
      ...prev,
      page: 0,
    }));
  }, [selectedGrade]);

  const handleBack = () => {
    setIsFilesView(false);
    setShouldRenderFolders(true);
    setIsFoldersFading(false);
    dispatch(setSelectedGrade(null));
    navigate("/students");
  };

  const handleRefresh = async () => {
    if (!selectedEvent || selectedGrade === null || !refreshUploads) return;

    try {
      await refreshUploads();
    } catch (error) {
      console.error("Error refreshing data:", error);
    }
  };

  const handleGradeClick = (grade, element) => {
    dispatch(setSelectedGrade(grade));
    setIsFoldersFading(true);

    if (element && typeof window !== "undefined") {
      const iconElement = element.querySelector("img");
      const sourceRect =
        iconElement?.getBoundingClientRect() || element.getBoundingClientRect();

      if (!sourceRect) {
        setShouldRenderFolders(false);
        setIsFilesView(true);
        setIsFoldersFading(false);
        setOverlayRect(null);
        return;
      }

      const contentRect = contentRef.current?.getBoundingClientRect();
      const targetWidth = contentRect?.width ?? window.innerWidth ?? 0;
      const targetHeight = contentRect?.height ?? window.innerHeight ?? 0;
      const targetLeft = contentRect?.left ?? 0;
      const targetTop = contentRect?.top ?? 0;
      const targetCenterX = targetLeft + targetWidth / 2;
      const targetCenterY = targetTop + targetHeight / 2;
      const centerX = sourceRect.left + sourceRect.width / 2;
      const centerY = sourceRect.top + sourceRect.height / 2;
      const translateX = targetCenterX - centerX;
      const translateY = targetCenterY - centerY;

      setOverlayRect({
        left: sourceRect.left,
        top: sourceRect.top,
        width: sourceRect.width,
        height: sourceRect.height,
        translateX,
        translateY,
      });
    } else {
      setShouldRenderFolders(false);
      setIsFilesView(true);
      setOverlayRect(null);
      setIsFoldersFading(false);
    }
  };

  const handleOverlayComplete = () => {
    setShouldRenderFolders(false);
    setIsFilesView(true);
    setOverlayRect(null);
    setIsFoldersFading(false);
  };

  const handleBackToFolders = () => {
    setIsFilesView(false);
    setIsFoldersFading(false);
  };

  const handleFilesExitComplete = () => {
    dispatch(setSelectedGrade(null));
    setFoldersKey((key) => key + 1);
    setShouldRenderFolders(true);
    setIsFoldersFading(false);
  };

  const handleUploadSuccess = () => {
    setShowToast(true);
    setToastMessage("The file is uploaded successfully!");
    setToastType("success");
    handleRefresh();
    setTimeout(() => setShowToast(false), 3000);
  };

  const handlePaginationModelChange = (model) => {
    setPaginationModel(model);
  };

  const schoolAddress = formatSchoolAddress(selectedSchool);
  const schoolId = selectedSchool?.school_id || selectedSchool?.id || "N/A";
  const eventName = selectedEvent?.event || "N/A";

  return (
    <div className={styles["pdf-upload-page"]}>
      {/* Header */}
      <div className={styles["page-header"]}>
        <div className={styles["page-header-left"]}>
          <IconButton
            onClick={handleBack}
            className={styles["back-button"]}
            aria-label="Go back"
          >
            <img src={backArrowIcon} alt="Back" />
          </IconButton>
          <div className={styles["page-header-text"]}>
            PDF Upload
            <p className={styles["page-subtitle"]}>
              Upload and manage grade-wise OMR PDFs
            </p>
          </div>
        </div>
        <div className={styles["page-header-actions"]}>
          <motion.button
            type="button"
            onClick={handleRefresh}
            className={styles["refresh-button"]}
            aria-label="Refresh"
            disabled={isUploadsLoading || selectedGrade === null}
            style={{ originX: 0.5, originY: 0.5 }}
            whileHover={
              !isUploadsLoading && selectedGrade !== null
                ? {
                    scale: 1.03,
                    boxShadow: "0 6px 18px rgba(0, 123, 255, 0.25)",
                  }
                : undefined
            }
            whileTap={
              !isUploadsLoading && selectedGrade !== null
                ? { scale: 0.98 }
                : undefined
            }
            transition={{ type: "spring", stiffness: 320, damping: 22 }}
          >
            <span className={styles["refresh-text"]}>Refresh</span>
            <RefreshIcon
              style={{ width: "24px", height: "24px", flexShrink: 0 }}
            />
          </motion.button>
          <button
            type="button"
            onClick={() => setIsUploadDialogOpen(true)}
            className={styles["upload-omr-button"]}
          >
            Upload OMR PDF
          </button>
        </div>
      </div>

      {/* Details Section */}
      <div className={styles["details-wrapper"]}>
        <div className="school-details-section">
          {`School: ${schoolAddress} | School ID: ${schoolId} | Event: ${eventName}`}
        </div>
      </div>

      {/* Content Area */}
      <div ref={contentRef} className={styles["pdf-upload-content"]}>
        {isUploadsLoading && (
          <div className={styles["loading-container"]}>
            <img src={loader} alt="Loading" />
          </div>
        )}

        {!isUploadsLoading && shouldRenderFolders && (
          <GradeFolders
            key={foldersKey}
            grades={grades}
            onGradeClick={handleGradeClick}
            replayKey={foldersKey}
            isFadingOut={isFoldersFading}
          />
        )}

        <AnimatePresence mode="wait" onExitComplete={handleFilesExitComplete}>
          {!isUploadsLoading && isFilesView && selectedGrade !== null && (
            <motion.div
              key="files-view"
              className={styles["grade-files-view"]}
              initial={{ scale: 1.15, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.85, opacity: 0 }}
              transition={{
                duration: 0.4,
                type: "spring",
                bounce: 0.2,
                damping: 22,
              }}
            >
              <GradeFilesTable
                files={files}
                grade={selectedGrade}
                onBack={handleBackToFolders}
                onRefresh={handleRefresh}
                paginationModel={paginationModel}
                onPaginationChange={handlePaginationModelChange}
                rowCount={totalCount}
                isLoading={isUploadsLoading}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {overlayRect && (
          <motion.img
            src={folderIcon}
            alt=""
            aria-hidden="true"
            className={styles["folder-dive-overlay-icon"]}
            style={{
              left: overlayRect.left,
              top: overlayRect.top,
              width: overlayRect.width,
              height: overlayRect.height,
            }}
            initial={{ x: 0, y: 0, scale: 1, opacity: 1 }}
            animate={{
              x: overlayRect.translateX,
              y: overlayRect.translateY,
              scale: 10,
              opacity: 0,
            }}
            transition={{
              type: "spring",
              stiffness: 360,
              damping: 32,
              mass: 0.8,
              velocity: 2,
            }}
            onAnimationComplete={handleOverlayComplete}
          />
        )}
      </div>

      {/* Upload Dialog */}
      <OmrUploadDialog
        isOpen={isUploadDialogOpen}
        onClose={() => setIsUploadDialogOpen(false)}
        onSuccess={handleUploadSuccess}
      />

      {/* Toast Notification */}
      {showToast && (
        <NotificationPortal>
          <div
            className={`${styles["pdf-upload-toast"]} ${
              styles[`pdf-upload-toast--${toastType}`] || ""
            }`}
            role={toastType === "success" ? "status" : "alert"}
            aria-live={toastType === "success" ? "polite" : "assertive"}
          >
            <button
              type="button"
              className={styles["pdf-upload-toast-close"]}
              onClick={() => setShowToast(false)}
              aria-label="Dismiss message"
            >
              ×
            </button>
            <p className={styles["pdf-upload-toast-text"]}>{toastMessage}</p>
          </div>
        </NotificationPortal>
      )}
    </div>
  );
};

export default PdfUploadPage;
